export const common = {
//   SECTION 1  — PhenoAge Header Dialog
//   SECTION 2  — Biological Age Card
//   SECTION 3  — Pace of Aging Card
//   SECTION 4  — Mortality Risk Card
//   SECTION 5  — Aging Drivers Section
//   SECTION 6  — Biological Age Trend Card
//   SECTION 7  — Biomarker Cards (Tornado Chart dialogs)
//   SECTION 8  — Shared UI Strings
//   SECTION 9  — Info Tooltips & Subheadings (Cockpit cards)
// ============================================================

// ── SECTION 1 · PhenoAge Header Dialog ──────────────────────

"age_1": "BIOLOGICAL AGE - ",
"age_2": "How old are you?",
"age_9": "Biological Age Cockpit ",
"age_10": "BIOLOGICAL AGE",
"phenoage_biomarkers_header": "Biological Age is calculated from 9 blood biomarkers",
"age_11": "years",
"age_12": "CHRONOLOGICAL AGE: ",
"age_13": "Age Diff.",
"age_14": "since last test",
"age_15": "PACE OF AGING",
"age_16": "MORTALITY RISK",
"age_17": "Aging Drivers",
"age_18": "Accelerates aging",
"age_19": "Slows aging",
"age_20": "Biological Age Trend",
"bioage_title_bioage": "Biological age",
"bioage_title_pace": "Pace of aging",
"bioage_title_mortality": "Mortality risk (10 years)",
"bioage_chrono_age": "Chronological age",
"bioage_snapshots": "{n} measurements",
"bioage_info_recommendation": "Recommendation",

"age_3": "PhenoAge is a scientifically validated measure of how old your body is on the inside — independent of how many years you have been alive. It was developed by Dr. Morgan Levine at Yale University and is calculated from nine routine blood biomarkers together with your chronological age.",
"age_4": "PhenoAge uses nine biomarkers that are available from a standard blood panel: albumin, creatinine, glucose, C-reactive protein, lymphocyte percentage, mean corpuscular volume, red cell distribution width, alkaline phosphatase, and white blood cell count. A mathematical model combines these into a single biological age score.",
"age_5": "Chronological age is fixed — it is simply how many years you have lived. Biological age is modifiable. People who live healthily often have a biological age several years below their chronological age, meaning their body is functioning as if it were younger.",
"age_6": "Research published in Nature Medicine (Levine et al. 2018) showed that a higher PhenoAge is strongly linked to increased risk of cardiovascular disease, cancer, diabetes, and all-cause mortality — even after adjusting for chronological age. Every one-year reduction in PhenoAge is associated with meaningfully lower disease risk.",
"age_7": "A biological age lower than your chronological age is a positive sign. A biological age higher than your chronological age signals accelerated aging and warrants closer attention to lifestyle, metabolic health, and specific biomarkers.",
"age_8": "Focus on the biomarkers that contribute most to your biological age. Small, consistent changes in nutrition, physical activity, sleep, and stress management can shift your PhenoAge meaningfully over months.",

"age_4_label": "The algorithm",
"age_5_label": "Biological age vs. chronological age",
"age_6_label": "Research evidence",
"age_7_label": "How to interpret your result",

// ── SECTION 2 · Biological Age Card ─────────────────────────

"bioage_info_intro": "Your biological age is a measure of how well your body is functioning at a cellular and physiological level. It is calculated from nine blood biomarkers using the PhenoAge algorithm developed at Yale University.",
"bioage_section_what_label": "What is biological age?",
"bioage_section_what": "Biological age reflects the functional state of your body — how efficiently your organs, immune system, and metabolism are working — rather than how many years you have been alive. Two people of the same chronological age can have very different biological ages depending on their health, lifestyle, and genetics.",
"bioage_section_how_label": "How is it calculated?",
"bioage_section_how": "PhenoAge is calculated from nine biomarkers in a standard blood test: albumin, creatinine, glucose, C-reactive protein (CRP), lymphocyte percentage, mean corpuscular volume (MCV), red cell distribution width (RDW), alkaline phosphatase (ALP), and white blood cell count. These biomarkers capture information about inflammation, organ function, immune health, and metabolic health. A validated statistical model combines them into a single age score.",
"bioage_section_interpret_label": "How to interpret your result",
"bioage_section_interpret": "If your biological age is lower than your chronological age, your body is functioning younger than your years — a positive sign. If it is higher, your body is showing signs of accelerated aging. The goal is not a perfect score but an improving trend over time. A single result is a snapshot; the trend across multiple blood draws is the most meaningful signal.",
"bioage_section_rec": "Focus on the biomarkers with the highest contribution to your biological age first. Improving them through nutrition, exercise, sleep, and stress reduction can lower your biological age over time.",

// ── SECTION 3 · Pace of Aging Card ──────────────────────────

"pace_info_intro": "Pace of aging tells you how fast your body is currently aging relative to the average. A value of 1.0x means you are aging at exactly the average pace for your chronological age.",
"pace_section_what_label": "What does pace of aging mean?",
"pace_section_what": "Pace of aging is calculated by dividing your biological age by your chronological age. A result of 1.0x means your body is aging at the expected rate. A result above 1.0x means your body is aging faster than average — for example, 1.2x means your body is aging 20% faster than expected. A result below 1.0x means your body is aging more slowly than average.",
"pace_section_optimal_label": "Optimal range (below 1.0x)",
"pace_section_optimal": "A pace of aging below 1.0x means your biological age is lower than your chronological age — your body is functioning younger than your years. This is associated with lower risk of age-related diseases and better long-term health outcomes. Healthy lifestyle habits — regular exercise, a balanced diet, good sleep, and low chronic stress — are the main drivers of a low pace of aging.",
"pace_section_rec": "Maintain your current habits and continue monitoring your biomarkers. A consistently low pace of aging is one of the strongest indicators of long-term health.",
"pace_section_accelerated_label": "Accelerated aging (above 1.0x)",
"pace_section_accelerated": "A pace above 1.0x means your biological age is higher than your chronological age. The further above 1.0x, the more significant the acceleration. This does not mean your health cannot improve — biological age is modifiable, and many people successfully reverse accelerated aging through targeted lifestyle and medical interventions.",
"pace_section_accelerated_rec": "Identify the biomarkers contributing most to your elevated biological age and address them. Prioritise inflammation (CRP), metabolic markers (glucose), and nutritional status (albumin) as starting points.",

// ── SECTION 4 · Mortality Risk Card ─────────────────────────

"mortality_info_intro": "Mortality risk shows your estimated probability of dying from any cause within the next 10 years, based on your current PhenoAge biomarkers. It is a statistical estimate, not a certainty.",
"mortality_section_what_label": "What is 10-year mortality risk?",
"mortality_section_what": "This number is derived directly from the PhenoAge model. It uses your nine biomarkers and chronological age to estimate the percentage probability that a person with your biological profile would die from any cause within 10 years. It is a population-level statistical estimate based on large studies — it describes risk, not destiny. Many people with elevated risk scores live long, healthy lives by addressing their modifiable risk factors.",
"mortality_section_low_label": "Low risk (below 3%)",
"mortality_section_low": "A risk below 3% means your biomarker profile is similar to that of people who tend to have good long-term health outcomes. This is a reassuring result and suggests your body is functioning well for your age.",
"mortality_section_low_rec": "Continue your healthy habits and maintain regular blood monitoring. Even at low risk, tracking your trend over time helps you catch early changes before they become significant.",
"mortality_section_elevated_label": "Elevated risk (3% or above)",
"mortality_section_elevated": "A risk at or above 3% indicates that one or more of your biomarkers are outside their optimal range in a way that statistically increases risk. This is not a diagnosis — it is a signal that your body may benefit from attention. The most common modifiable contributors are chronic inflammation (high CRP), poor metabolic control (high glucose), low nutritional reserves (low albumin), and immune dysregulation.",
"mortality_section_elevated_rec": "Targeted improvements to your highest-impact biomarkers can meaningfully reduce your risk score over time. PhenoAge is designed to detect these patterns early, when intervention is most effective.",

// ── SECTION 5 · Aging Drivers Section ───────────────────────

"drivers_info_intro": "Aging drivers show you exactly which of your nine biomarkers are contributing most to your biological age — and by how much. This turns your PhenoAge score into an actionable list of priorities.",
"drivers_section_what_label": "What are aging drivers?",
"drivers_section_what": "The PhenoAge model assigns a specific contribution — measured in years — to each of your nine biomarkers. A positive contribution means that biomarker is currently adding years to your biological age. A negative contribution means it is reducing your biological age. The chart ranks them from the biggest contributor to the smallest, so you can see at a glance where the most leverage is.",
"drivers_section_positive_label": "Positive contributors (they add years)",
"drivers_section_positive": "When a biomarker has a positive contribution, its current value is in a range that the model associates with an older biological age. For example, a high glucose, a high CRP, or a low albumin will each add years to your PhenoAge. The larger the bar, the bigger the effect of that biomarker on your score.",
"drivers_section_positive_rec": "Tap any bar in the chart to see the current value, the optimal target, and a specific action plan for that biomarker. Focus on the largest positive contributors first for the biggest impact.",
"drivers_section_negative_label": "Negative contributors (they subtract years)",
"drivers_section_negative": "When a biomarker has a negative contribution, its current value is in a range that the model associates with a younger biological age. These are your biological age assets — they are actively working in your favour. The goal is to protect these and move the positive contributors toward the negative side over time.",
"drivers_section_action_label": "What can I do?",
"drivers_section_action": "Each biomarker can be influenced by specific lifestyle and medical interventions. Tap any bar in the chart to see recommendations for that marker. No single change fixes everything — consistent, targeted improvements across several biomarkers compound over time into a meaningfully lower biological age.",
"drivers_section_action_rec": "Start with the one or two biomarkers with the highest positive contribution. Small, sustainable changes outperform dramatic short-term efforts.",

// ── SECTION 6 · Biological Age Trend Card ───────────────────

"trend_info_intro": "The biological age trend shows how your PhenoAge has changed across all your blood draws. It is the most important view in the cockpit — a single result is a snapshot, but the trend reveals whether your interventions are working.",
"trend_section_what_label": "What does the trend show?",
"trend_section_what": "The chart plots your biological age from each blood draw against your chronological age at the same point in time. The gap between the two lines tells you whether your biological age is diverging from or converging toward your chronological age. A narrowing gap — or a biological age line that runs below the chronological age line — means your body is aging well.",
"trend_section_improving_label": "Improving trend",
"trend_section_improving": "An improving trend means your biological age is decreasing relative to your chronological age over time — your interventions are working. This can happen even if individual measurements fluctuate. What matters is the direction over multiple blood draws.",
"trend_section_improving_rec": "Keep doing what is working. Continue monitoring at regular intervals (every 3–6 months is ideal) to confirm the trend and detect any regressions early.",
"trend_section_worsening_label": "Worsening or stagnant trend",
"trend_section_worsening": "A worsening trend means your biological age is increasing faster than your chronological age over time. A flat trend means your interventions are maintaining but not improving your biological age. Both are signals to review which biomarkers have shifted and why.",
"trend_section_worsening_rec": "Re-examine your current habits and any recent changes to your health, diet, medications, or stress levels. Compare the aging drivers chart across different blood draws to identify which biomarkers have deteriorated and focus your efforts there.",

// ── SECTION 6.5 · Marker Names (short + long) ──────
// Keys follow the pattern: markername_short_KEY / markername_long_KEY
// KEY = abbreviation uppercased, max 6 chars, spaces → underscore (matches marker_desc_KEY etc. below)

"markername_short_ALB": "Albumin",
"markername_long_ALB": "Albumin",
"markername_short_CRE": "Creatinine",
"markername_long_CRE": "Creatinine",
"markername_short_GLU": "Glucose",
"markername_long_GLU": "Glucose",
"markername_short_LYM": "Lymphocytes",
"markername_long_LYM": "Lymphocytes",
"markername_short_MCV": "MCV",
"markername_long_MCV": "MCV",
"markername_short_CRP": "hsCRP",
"markername_long_CRP": "hsCRP",
"markername_short_RDW": "RDW",
"markername_long_RDW": "Red Cell Distribution Width",
"markername_short_ALP": "Alk. Phos",
"markername_long_ALP": "Alkaline Phosphatase",
"markername_short_WBC": "WBC",
"markername_long_WBC": "White Blood Cell",

// ── SECTION 7 · Biomarker Cards (Tornado Chart dialogs) ──────
// Keys follow the pattern: marker_desc_KEY, what_it_does_KEY, action_KEY
// KEY = abbreviation uppercased, max 6 chars, spaces → underscore

// · Albumin (ALB)
"marker_desc_ALB": "Albumin is the most abundant protein in your blood. It is made by the liver and serves as a carrier for hormones, vitamins, and other substances throughout your body. It also plays a key role in maintaining the balance of fluid between your blood vessels and tissues.",
"what_it_does_ALB": "Low albumin is one of the strongest signals of poor overall health in the PhenoAge model. It reflects reduced liver function, chronic inflammation, malnutrition, or a combination of these. Because albumin is produced by a healthy, well-nourished liver, a low level suggests that something is disrupting that process — whether poor protein intake, ongoing inflammation, liver stress, or illness.",
"action_ALB": "Ensure you are eating adequate protein (1.2–1.6 g per kg of body weight per day for most adults). Prioritise whole food sources such as eggs, fish, meat, legumes, and dairy. Address any underlying inflammation by reducing ultra-processed foods, alcohol, and chronic stress. If albumin is persistently low, discuss it with your doctor to rule out liver or kidney conditions.",

// · Creatinine (CRE)
"marker_desc_CRE": "Creatinine is a waste product produced naturally by your muscles during normal activity. It is filtered out of your blood by your kidneys and excreted in urine. Measuring creatinine is one of the most reliable ways to assess how well your kidneys are filtering your blood.",
"what_it_does_CRE": "In the PhenoAge model, creatinine reflects kidney filtration efficiency. Higher creatinine indicates reduced kidney function, which is associated with accelerated biological aging. Kidney function declines gradually with age, but this decline can be accelerated by high blood pressure, diabetes, chronic dehydration, and certain medications. Importantly, very low creatinine can also be a concern as it may reflect low muscle mass.",
"action_CRE": "Stay well hydrated — adequate water intake supports kidney filtration. Control blood pressure and blood sugar, which are the two leading causes of kidney decline. Avoid excessive use of non-steroidal anti-inflammatory drugs (NSAIDs) such as ibuprofen. If your creatinine is elevated, your eGFR (estimated glomerular filtration rate) should be checked for a more precise picture of kidney function.",

// · Glucose (GLU)
"marker_desc_GLU": "Glucose is the primary sugar in your blood and the main source of energy for your cells. Your body works continuously to keep blood glucose within a narrow range. A fasting blood glucose measurement — taken after at least 8 hours without eating — reflects your body's baseline ability to regulate blood sugar without the influence of recent food.",
"what_it_does_GLU": "Elevated fasting glucose is a marker of impaired metabolic health. It indicates that your body is either not producing enough insulin, not responding to insulin efficiently, or both — a condition known as insulin resistance. Chronically elevated glucose accelerates biological aging through multiple pathways: it promotes inflammation, damages blood vessels, impairs organ function, and directly increases the PhenoAge score.",
"action_GLU": "Reduce intake of refined carbohydrates and added sugars — these cause the largest spikes in blood glucose. Prioritise fibre-rich foods, vegetables, legumes, and whole grains, which slow glucose absorption. Regular aerobic exercise (150 minutes per week) and resistance training both significantly improve insulin sensitivity. Aim for 7–9 hours of quality sleep per night, as poor sleep directly raises fasting glucose.",

// · Lymphocyte % (LYM)
"marker_desc_LYM": "Lymphocytes are a type of white blood cell that form the backbone of your adaptive immune system. They include T-cells and B-cells, which recognise and destroy specific threats such as viruses, bacteria, and abnormal cells. The lymphocyte percentage is the proportion of lymphocytes among all your white blood cells.",
"what_it_does_LYM": "In the PhenoAge model, a lower lymphocyte percentage is associated with accelerated biological aging. As we age, the immune system gradually becomes less efficient — producing fewer effective lymphocytes and more inflammatory signals. This process, called immunosenescence, is a hallmark of biological aging. A low lymphocyte percentage can also reflect chronic stress, poor nutrition, or underlying immune suppression.",
"action_LYM": "Support your immune system with adequate protein, zinc, vitamin D, and vitamin C — all of which are essential for lymphocyte production and function. Manage chronic stress, as elevated cortisol directly suppresses lymphocyte counts. Regular moderate exercise strengthens immune function, while very intense overtraining can suppress it. Ensure sufficient sleep, which is when immune memory and repair occur. Avoid smoking, as it accelerates immune aging.",

// · Mean Corpuscular Volume (MCV)
"marker_desc_MCV": "Mean corpuscular volume (MCV) measures the average size of your red blood cells. Red blood cells carry oxygen from your lungs to every cell in your body. Their size reflects the quality of red blood cell production and is influenced by your nutritional status and bone marrow function.",
"what_it_does_MCV": "In the PhenoAge model, an elevated MCV — larger-than-average red blood cells — is associated with accelerated biological aging. High MCV most commonly signals a deficiency in vitamin B12 or folate, both of which are essential for normal cell division in the bone marrow. It can also indicate alcohol overuse, thyroid dysfunction, or certain medications. Very low MCV indicates iron deficiency anaemia. Both extremes reflect suboptimal cell production.",
"action_MCV": "If MCV is high, check your vitamin B12 and folate levels. Good food sources of B12 include meat, fish, eggs, and dairy; if you are vegetarian or vegan, supplementation might be necessary. Folate is found in green leafy vegetables, legumes, and fortified foods. Reduce or eliminate alcohol, which directly raises MCV and impairs folate metabolism. If MCV is low, check iron and ferritin levels.",

// · C-Reactive Protein (CRP)
"marker_desc_CRP": "C-reactive protein (CRP) is a protein produced by your liver in response to inflammation anywhere in the body. It rises rapidly when your immune system is activated — whether by infection, injury, or chronic low-grade inflammation. In the context of PhenoAge, the high-sensitivity CRP (hsCRP) measurement is used, which detects even small amounts of chronic background inflammation.",
"what_it_does_CRP": "CRP is one of the most powerful contributors to biological age in the PhenoAge model. Chronic low-grade inflammation — even at levels that cause no obvious symptoms — accelerates cellular aging, damages blood vessels, disrupts metabolism, and impairs immune function. Elevated CRP is associated with higher risk of cardiovascular disease, type 2 diabetes, cancer, and neurodegeneration. It is also a common consequence of poor diet, excess body fat (particularly visceral fat), poor sleep, and chronic stress.",
"action_CRP": "The single most impactful change is reducing visceral (abdominal) body fat through a combination of caloric balance and regular exercise. Adopt an anti-inflammatory diet: prioritise vegetables, oily fish, olive oil, nuts, and legumes while reducing ultra-processed foods, refined sugars, and trans fats. Aim for 7–9 hours of quality sleep. Manage chronic stress through regular physical activity, mindfulness, or other evidence-based approaches. Avoid smoking. If CRP remains elevated without a clear cause, your doctor should evaluate for underlying conditions.",

// · Red Cell Distribution Width (RDW)
"marker_desc_RDW": "Red cell distribution width (RDW) measures how uniform your red blood cells are in size. A low RDW means your red blood cells are all roughly the same size — a sign of healthy, consistent cell production. A high RDW means there is significant variation in size, which signals that your bone marrow is struggling to produce red blood cells normally.",
"what_it_does_RDW": "In the PhenoAge model, a high RDW is one of the strongest contributors to accelerated biological aging. Elevated RDW reflects nutritional deficiencies (particularly iron, B12, and folate), chronic inflammation, oxidative stress, and impaired bone marrow function. Research has shown that high RDW predicts mortality across a wide range of diseases, independent of other factors. It is often an early warning sign of underlying problems before other markers become abnormal.",
"action_RDW": "Address the most common nutritional causes: ensure adequate iron, vitamin B12, and folate intake through food or supplementation. Check ferritin (stored iron), B12, and folate levels to confirm which, if any, are deficient. Reduce or eliminate alcohol, which directly elevates RDW.",

// · Alkaline Phosphatase (ALP)
"marker_desc_ALP": "Alkaline phosphatase (ALP) is an enzyme found in several tissues throughout the body, but primarily in the liver, bones, kidneys, and digestive tract. It plays a role in breaking down proteins and is released into the bloodstream when these tissues are under stress or actively remodelling.",
"what_it_does_ALP": "In the PhenoAge model, elevated ALP is associated with accelerated biological aging. High ALP most commonly reflects liver stress (from fatty liver, alcohol, or medications), increased bone turnover, or biliary tract issues. Low ALP can indicate malnutrition or zinc deficiency. Because ALP comes from multiple tissues, an abnormal result requires further investigation to identify the source. It is a useful early warning signal for metabolic and hepatic health.",
"action_ALP": "If ALP is elevated, avoid or minimise alcohol, which is a leading cause of liver-related ALP elevation. Reduce ultra-processed food intake and maintain a healthy body weight to protect against fatty liver disease. Ensure adequate vitamin D and calcium for bone health, as deficiencies can elevate bone-derived ALP.",

// · White Blood Cell Count (WBC)
"marker_desc_WBC": "White blood cells (WBC) are the cells of your immune system. They patrol your blood and tissues, defending your body against infections, foreign substances, and abnormal cells. The white blood cell count measures the total number of these immune cells in a given volume of blood.",
"what_it_does_WBC": "In the PhenoAge model, both elevated and slightly elevated WBC counts contribute to accelerated biological aging. A high WBC count often reflects chronic low-grade inflammation or immune activation — meaning your immune system is persistently in a state of alert even without an acute infection. Over time, this chronic immune activation accelerates cellular aging, damages tissues, and contributes to cardiovascular disease, diabetes, and other age-related conditions. A very low WBC may indicate immune suppression.",
"action_WBC": "Reduce the root causes of chronic immune activation: address excess body fat (especially visceral fat), manage chronic stress, prioritise sleep, and adopt an anti-inflammatory diet. Quit smoking, as it is one of the most reliable drivers of elevated WBC. Regular moderate aerobic exercise reduces chronic WBC elevation. If WBC is significantly elevated or very low without explanation, your doctor should investigate for infections, autoimmune conditions, or haematological causes.",

// ── SECTION 8 · Shared UI Strings ───────────────────────────

"tornado_title": "Aging Drivers",
"tornado_subtitle": "Sensitivity analysis — tap any bar for details",
"tornado_legend_adds": "Adds years",
"tornado_legend_subtracts": "Subtracts years",
"tornado_click_hint": "Tap a bar for details",
"tornado_top_drivers": "Top Aging Drivers",
"tornado_impact_years": "Impact (years)",

"tornado_dialog_subtitle": "Aging Driver Detail",
"tornado_dialog_description_label": "About this marker",
"tornado_dialog_what_it_does_label": "What it does",
"tornado_dialog_how_to_optimize_label": "How to optimize",
"tornado_dialog_action_label": "How to optimize",
"tornado_dialog_impact_negative": "added to biological age",
"tornado_dialog_impact_positive": "reduces biological age",
"tornado_dialog_years": "years",
"tornado_dialog_current": "Current",
"tornado_dialog_optimized": "Optimized",
"tornado_dialog_savings": "Savings",
"tornado_dialog_no_detail": "Details not yet available for this marker.",

// ── SECTION 9 · Info Tooltips & Subheadings (Cockpit cards) ─

// PhenoAge info dialog tab labels
"age_info_formula_label": "Formula",
"age_info_biomarkers_label": "Biomarkers",
"age_info_pace_label": "Pace of Aging",
"age_info_mortality_label": "Mortality Risk",
"age_info_drivers_label": "Aging Drivers",
"age_info_trend_label": "Trend",

// Biological Age card
"bioage_subheading": "How old your body's cells and organs truly are",
"bioage_info_tooltip": "What is Biological Age?",
"bioage_info_content": "Biological Age measures the functional age of your body at a cellular and physiological level — independent of how many years you have lived. It is calculated using PhenoAge, a validated algorithm developed by Dr. Morgan Levine at Yale University. The algorithm analyses nine blood biomarkers (albumin, creatinine, glucose, C-reactive protein, lymphocyte percentage, mean corpuscular volume, red cell distribution width, alkaline phosphatase, and white blood cell count) together with chronological age to produce a single composite score.\n\nWhy it matters for preventive medicine: Chronological age is fixed — biological age is modifiable. Research shows that individuals with a biological age significantly below their chronological age have substantially lower risks of cardiovascular disease, cancer, diabetes, and all-cause mortality. A one-year reduction in biological age is associated with a measurable reduction in disease risk across multiple organ systems.\n\nHow to interpret your result: A biological age lower than your chronological age is a positive sign — your body is ageing more slowly than average. A higher biological age signals accelerated ageing and warrants closer attention to lifestyle, metabolic health, and specific biomarkers. The Age Difference chip (e.g. −8.6) shows how many years ahead of or behind the population average your body currently is.",

// Pace of Aging card
"pace_subheading": "How fast you are ageing right now",
"pace_info_tooltip": "What is Pace of Aging?",
"pace_info_content": "Pace of Aging (also called Rate of Ageing) expresses how quickly your biological age is advancing relative to your chronological age. It is calculated as: Biological Age ÷ Chronological Age.\n\nA value of 1.0 means your body ages in perfect lockstep with calendar time — exactly as expected for the average person. A value below 1.0 means you are ageing slower than time itself. A value above 1.0 means your body is accumulating biological wear faster than the calendar advances.\n\nWhy it matters for preventive medicine: The pace of ageing is one of the most actionable longevity metrics available. Unlike a static snapshot, it reflects the dynamic trajectory of your health. Studies from the Dunedin cohort (New Zealand) show that individuals with a higher pace of ageing in their 30s and 40s already display worse physical function, cognitive decline, and organ reserve decades before clinical disease appears. Even a modest reduction in pace — moving from 1.1 to 0.95, for example — has been modelled to compress morbidity and extend healthspan by several years.\n\nHow to interpret your result: Aim for a value below 1.0. Values between 0.85 and 0.95 are typically seen in people with excellent metabolic health, regular physical activity, and low chronic inflammation. Values above 1.1 warrant investigation of the specific biomarkers driving the acceleration.",

// Mortality Risk card
"mortality_subheading": "Estimated 10-year all-cause mortality probability",
"mortality_info_tooltip": "What is Mortality Risk?",
"mortality_info_content": "Mortality Risk is a statistically derived estimate of the probability of dying from any cause within the next 10 years, based on your current PhenoAge score. It is calculated using a proportional hazards model fitted to the NHANES III cohort (a large, nationally representative US population study) and subsequently validated in multiple independent cohorts.\n\nThis is not a diagnosis or a prediction for any individual — it is a population-level risk estimate that quantifies where you stand relative to others with the same biological profile.\n\nWhy it matters for preventive medicine: Mortality risk quantifies the real-world consequences of your current biological age in a single, intuitive number. It bridges the gap between abstract biomarker scores and clinically meaningful outcomes. A low mortality risk (< 1–2 %) confirms that your current biological status corresponds to a very healthy ageing trajectory. Elevated values (> 5 %) signal that physiological changes are already measurable and that targeted intervention — in diet, exercise, sleep, stress management, or specific medical treatment — could meaningfully reduce risk.\n\nHow to interpret your result: Values below 1 % are excellent and typically correspond to a biological age substantially younger than chronological age. Values of 1–3 % are within a healthy range for most age groups. Values above 5 % indicate accelerated biological ageing and should prompt a detailed review of the individual biomarkers contributing to your PhenoAge score.",

// Aging Drivers card
"drivers_subheading": "The specific blood markers pushing your biological age up or down",
"drivers_info_tooltip": "What are Aging Drivers?",
"drivers_info_content": "Aging Drivers shows the individual contribution of each of the nine PhenoAge biomarkers to your current biological age — expressed in years gained or saved. A red bar means that biomarker is currently adding years to your biological age; a green bar means it is pulling your biological age down relative to average.\n\nThe nine drivers and what they reflect:\n• Albumin — liver function and nutritional status; low values reflect chronic inflammation or poor protein intake.\n• Creatinine — kidney filtration capacity; elevated values indicate reduced renal function.\n• Glucose — metabolic health and insulin sensitivity; chronically elevated glucose accelerates glycation and cellular ageing.\n• C-reactive protein (CRP) — systemic inflammation; the single most modifiable driver for most people.\n• Lymphocyte % — immune system vitality; low levels suggest immune senescence or chronic stress.\n• Mean Corpuscular Volume (MCV) — red blood cell size, linked to B12/folate status and alcohol intake.\n• Red Cell Distribution Width (RDW) — red blood cell variability; a powerful independent predictor of biological age and mortality.\n• Alkaline Phosphatase (ALP) — liver and bone metabolism; elevated levels correlate with metabolic dysfunction.\n• White Blood Cell Count (WBC) — immune activation and chronic low-grade infection.\n\nWhy it matters for preventive medicine: The drivers section transforms your PhenoAge score from a single number into an actionable roadmap. Instead of knowing only that your biological age is elevated, you can see precisely which organ systems or metabolic processes are responsible — and target your interventions accordingly.\n\nHow to interpret your result: Focus first on the largest red bars. Click any bar or marker row to see your current value, the optimal target value, and the potential years you could recover by optimising that marker.",
"drivers_detail_section_label": "Marker details",

// Biological Age Trend card
"trend_subheading": "How your biological age has changed across all your lab visits",
"trend_info_tooltip": "What is the Biological Age Trend?",
"trend_info_content": "The Biological Age Trend chart plots your PhenoAge score over time, alongside your chronological age as a reference line. Each data point represents a lab visit snapshot.\n\nThe orange line is your biological age trajectory — the single most important longevity signal visible in this chart. The grey line is your chronological age, which advances at exactly 1 year per year by definition. Every time the orange line stays flat or slopes downward while the grey line rises, your body is effectively getting younger relative to calendar time.\n\nWhy it matters for preventive medicine: A single measurement tells you where you are. The trend tells you where you are going. Longitudinal tracking is essential because biological age fluctuates with acute illness, seasonal changes, and lifestyle shifts. A consistent downward trend over 12–24 months is strong evidence that your interventions — whether dietary, pharmacological, or exercise-based — are working at a systems biology level. A rising trend, even from a low baseline, is an early warning signal that warrants investigation before symptoms appear.\n\nHow to interpret your result: Compare the slope of your orange line to the 45-degree slope of the grey chronological line. A shallower slope means you are ageing more slowly than time. A steeper slope means acceleration. The delta chip (e.g. −2.3 yrs since first test) summarises the net change across your entire measurement window. Click any point on the chart or any snapshot pill below to explore the detailed drivers for that specific visit.",



//######### LOGIN SCREEN ###############
"login_welcome": "Welcome to speak23",
"login_headline": "Welcome",
"login_subheadline": "Sign in to your account",
"login_username": "username",
"login_pw": "password",
"login_acc": "No account yet?",
"login_forgot": "Forgot Password?",
"login_reg": "Register",
"login_reset": "Reset Password",
"login_lang": "Change Language",
"login_signin": "SIGN IN",

//######### RESET PASSWORD ###############
"reset_1": "Forgot Password?",
"reset_2": "Please enter your registered email address to reset your password.",
"reset_3": "Send Reset Link",
"reset_4": "New Password",
"reset_5": "Please set your new Password.",
"reset_6": "Set new Password",
"reset_7": "Confirm new Password",
"reset_8": "Reset link sent. Please check your inbox.",
"reset_9": "This reset link has expired. Please request a new one.",
"reset_10": "This reset link is invalid.",
"reset_11": "The reset link is invalid or expired.",
"reset_12": "Your password has been reset successfully.",

//######### REGISTER SCREEN ###############
"reg_lang": "preferred language",
"reg_en": "'🇬🇧' English",
"reg_de": "'🇩🇪' German",
"reg_username": "username",
"reg_pw": "password",
"reg_pwr": "repeat password",
"reg_first": "First name",
"reg_last": "Last name",
"reg_mobile": "Mobile phone",
"reg_tc": "I accept the terms & conditions",
"reg_reg": "Already registered?",
"reg_tcr": "Read the T&Cs here:",
"reg_headline": "Please Register",
"reg_submit": "SUBMIT",
"reg_signin": "SIGN IN",

"val_username_chars": "Username may only contain letters, digits, and @/./+/-/_ characters",
"val_username_min": "Username must be at least 3 characters",
"val_username_max": "Username cannot exceed 150 characters",
"val_username_req": "Username is required",
"val_email_valid": "Enter a valid email address",
"val_email_max": "Email cannot exceed 254 characters",
"val_email_req": "Email is required",
"val_pw_strong": "Password must be at least 8 characters and include uppercase, lowercase, a number and a special character",
"val_pw_req": "Password is required",
"val_pw2_match": "Passwords do not match",
"val_pw2_req": "Please confirm your password",
"val_old_pw_req": "Please enter your current password",
"pw_strength_too_weak":      "Too weak",
"pw_strength_weak":          "Weak",
"pw_strength_fair":          "Fair",
"pw_strength_good":          "Good",
"pw_strength_strong":        "Strong",
"pw_strength_excellent":     "Excellent",
"val_first_max": "First name cannot exceed 150 characters",
"val_first_req": "First name is required",
"val_last_max": "Last name cannot exceed 150 characters",
"val_last_req": "Last name is required",
"val_phone": "Enter a valid phone number (e.g. +123456789)",
"val_terms_req": "You must accept the terms and conditions",
"val_form_error": "Form Submission Error",
"val_form_fix": "Please fix the errors above.",
"val_server_error": "Server error. Please try again later.",
"val_login_invalid": "Invalid username or password.",
"val_required": "Required",
"val_date_past": "Date cannot be in the past",
"val_time_req": "Time is required",
"val_success": "Success",
"val_gender_invalid": "Invalid selection",
"val_birthday_future": "Birthday cannot be in the future",

"onb_email_title": "Please confirm your email address",
"onb_email0": "Thank you for signing up for speak23.",
"onb_email1": "Please verify your email address by confirming the link sent to you. ",
"onb_email2": "The provided link will be valid for 60 minutes.",
"onb_email3": "After receiving the success message continue with your first sign in.",
"onb_email4": "No link received?",
"onb_email5": "Please check also your spam folder.",
"onb_email5b": "In rare cases it may take several minutes for the email to arrive.",
"onb_email6": "Send link again",
"onb_email7": "Email verified?",
"onb_email8": "You are ready to sign in now with your credentials.",
"onb_email9": "Go to login",

//######### REGISTER SCREEN ###############
"onbsub_brandtext": "... more healthy years",
"onbsub_step1": "Select number of tests & secure payment",
"onbsub_step2": "Select lab, appointment & optional add-on tests",
"onbsub_tooltip_core_test": "More Info about Core Health Blood Test",
"onbsub_review_heading": "Review your selection and choose \"SECURE PAYMENT\"",
"onbsub_freq_question": "How often do you want to test?",
"onbsub_freq_2x": "2 times per year (semi-annually)",
"onbsub_freq_4x": "4 times per year (every 3 months)",
"onbsub_pay_question": "How do you want to pay?",
"onbsub_pay_label_full": "annual membership price",
"onbsub_pay_label_monthly": "per month - annual membership price billed in 12 monthly installments",
"onbsub_pay_title_full": "Pay annually",
"onbsub_pay_title_monthly": "Pay monthly",
"onbsub_save_badge": "Save € {{amount}},-",
"onbsub_summary_title": "Order Summary",
"onbsub_summary_subscription": "Annual Membership - includes {{count}} Core Health Blood Tests",
"onbsub_summary_visits_label": "Number of lab visits:",
"onbsub_summary_total": "Total",
"onbsub_note_tests_included": "Annual membership price includes {{count}} Core Health Blood Tests",
"onbsub_note_vat": "All prices include 20% VAT",
"onbsub_note_term": "The initial term of the subscription is 12 months.",
"onbsub_note_renewal": "After the initial term ends, your subscription automatically renews for another year.",
"onbsub_note_cancel": "After the initial term, you can cancel the subscription at any time in accordance with the T&Cs.",

"onb_wel": "Welcome to speak23",
"onb_step1": "Subscription & select number of tests",
"onb_step2": "Secure Payment",
"onb_step3": "Select Lab and Date",
"onb_step4": "Complete a Short Questionnaire",
"onb_step5": "Labvisit",

"onb_start": "Step 1:",
"onb_start1": "Choose how often you want your Core Health Blood Test: 2 or 4 times per year",
"onb_start2": "Optionally add additional blood or stool tests as add-ons for your first lab visit",
"onb_start3": "Press 'SECURE PAYMENT' to complete Step 1",

"onb_subscrconf": "Annual Subscription",
"onb_subscrconf_sub": "Auto-Renewed yearly if not cancelled",
"onb_subscr1": "Annual Membership",
"onb_subscr5": "2 Core Health Blood Tests per year",
"onb_per_test": "per test - test every 6 months",
"onb_per_test2": "per test - test every 3 months",
"onb_offer_deadline": "Offer until June 14, 2026",
"onb_bio_age_incl": "Biological Age included",
"onb_subscr6": "Every 6 months — billed yearly at €",
"onb_subscr71": "Upgrade option",
"onb_subscr71_detail": "4 Core Health Blood Tests per year",
"onb_subscr8": "Every 3 months — upgrade billed yearly at €",
"onb_subscr10": "Annual Subscription: Total",
"onb_subscr11": "incl. VAT, billed yearly",

"onb_subscr12": "Optional: Select Add-On Packages",
"onb_subscr12_sub": "One-time purchase for your upcoming labvisit",
"onb_subscr121": "Optional: Add-On Test Packages (one-time)",
"addon_read_more": "Read more",
"onb_subscr13": "Blood Tests",
"onb_subscr14": "Stool Tests",
"onb_subscr15": "Selected Add-On Packages: Total",
"onb_subscr16": "incl. VAT, billed once",
"onb_subscr165": "Click SECURE PAYMENT to proceed to payment.",
"onb_btn_secure_payment": "SECURE PAYMENT",
"onb_msg_redirect": "Redirecting to secure checkout…",
"onb_error_stripe": "Stripe checkout could not be started. Please try again.",
"onb_error_checkout": "Checkout initialization failed. Please try again.",
"onbsub_error_missing_plan": "Please select a plan.",
"onbsub_error_invalid_plan": "Invalid plan selected.",
"onbgift_prompt": "Do you have a gift code?",
"onbgift_btn_open": "REDEEM GIFT CODE",
"onbgift_step1": "Redeem gift code",
"onbgift_field_label": "Gift code",
"onbgift_field_helper": "16 characters, e.g. AB3X-K9_QWZ7PLMN",
"onbgift_btn_submit": "SUBMIT",
"onbgift_no_code": "I don't have a gift code",
"onbgift_btn_back": "Back",
"onbgift_success_title": "Gift code redeemed",
"onbgift_success_text": "You have received an annual membership with 2 Core Health blood tests. Your membership is valid until {{date}}.",
"onbgift_success_no_cost": "There are no costs for you.",
"onbgift_btn_continue": "CONTINUE TO BOOKING",
"onbgift_error_not_eligible": "Gift codes can only be redeemed during initial sign-up.",
"onbgift_error_active_subscription": "You already have an active membership.",
"onbgift_error_not_found": "Invalid code – please check your input.",
"onbgift_error_already_redeemed": "This code has already been redeemed.",
"onbgift_error_not_payable": "Code not active yet – please try again later.",
"onbgift_error_expired": "This code has expired.",
"onbgift_error_rate_limit": "Too many attempts – please try again later.",
"onbgift_error_generic": "The code could not be redeemed. Please contact our support at office@speak23.com.",
"onb_subscr17": "Note: there will be 2 separate payments.",
"onb_subscr18": "Payment 1 — Annual subscription: €",
"onb_subscr19": "incl. VAT",
"onb_subscr20": "Payment 2 — Add-On packages (if selected): €",
"onb_subscr21": "incl. VAT",

"addonsel_step1_heading": "Choose optional add-on tests for your lab visit",
"addonsel_step2_heading": "Review your selection and pay with \"SECURE PAYMENT\"",
"addonsel_empty": "You haven't selected any add-on tests yet.",
"addonsel_err_400": "Please select at least one add-on test.",
"addonsel_err_403": "Your subscription isn't active yet.",
"addonsel_err_404": "We couldn't find your scheduled lab visit.",
"addonsel_products_err_load": "Unable to load add-on tests. Please try again later.",
"addonsel_note_next_visit": "Your selection of add-on packages applies to your next lab visit. For subsequent lab visits, you can choose no add-on packages or completely different ones. After payment, you will receive the invoice including VAT at your personal email address.",
"addonsel_intro_text": "Here you can choose additional add-on packages for your upcoming lab visit — on top of the Core Health Blood Test. If you don't want to add any extra tests this time, you can go straight to the questionnaire.",
"addonsel_skip_button": "Go straight to the questionnaire",
"addonsel_note_gutmicrobiome": "You've selected the Gut Microbiome Analysis — a great choice. Over the next few days you'll receive your stool test kit by mail, with easy-to-follow instructions. Please return the tube with your sample by mail using the enclosed envelope.",
"addonsel_note_ifob": "You've selected the Colon Cancer Prevention & Early Detection test. This test is a valuable and affordable addition to your preventive care! You'll receive the test tube with instructions directly at the lab during your lab visit. Please collect the stool sample at home according to the instructions. You can return the test tube to the lab by mail or bring it back in person.",

"select_all_men": "All (Male)",
"select_all_women": "All (Female)",
"deselect_all_men": "Clear (Male)",
"deselect_all_women": "Clear (Female)",

"add_bio": "Biological Age",
"add_vit": "Advanced Vitamins & Trace Elements",
"add_omega": "Omega-3-Index & Fatty Acid Profile",
"add_hormf": "Hormones & Fertility (Women)",
"add_hormm": "Energy, Libido & Hormonal Balance (Men)",
"add_psa": "Prostate Cancer Prevention & Early Awareness (Men)",
"add_thyr": "Thyroid Health",
"add_ath": "Atherosclerosis Prevention",
"add_bones": "Bones Health",
"add_ov": "Ovarian Reserve (Women)",
"add_gut": "Gut Microbiome Analysis",
"add_fob": "Colon Cancer Prevention & Early Awareness",

"onb_subscr00": "Schedule Your Lab Visit",
"onb_subscr22": "Your first lab appointment is booked. Well done!",
"onb_subscr23": "Please provide a few personal details to prepare for your visit.",
"onb_subscr24": "Select Sex",
"onb_subscr244": "Select Sex",
"onb_subscr245": "Male",
"onb_subscr246": "Female",
"onb_subscr247": "Other",
"onb_subscr25": "Date of Birth",

"onb_subscr26": "Almost done!",
"onb_subscr27": "The next step is your lab visit! Good luck!",
"onb_subscr28": "All required information has been sent to the lab reception.",
"onb_visit_date_label": "Date:",
"onb_visit_time_label": "Time:",
"onb_no_visits": "No scheduled lab visits found.",
"onb_subscr29": "To reschedule, please contact the lab reception directly.",
"onb_subscr30": "Order summary and payment confirmation:",
"onb_subscr31": "Your Membership",
"onb_subscr32": "Add-On packages for your next lab visit",
"onb_subscr33": "You'll receive an email notification",
"onb_subscr34": "once your results and personalized recommendations are ready.",
"onb_subscr35": "Your stool collection kit for the gut microbiome test is on its way.",
"onb_subscr36": "Activate your test kit with the 12-digit code here.",
"onb_subscr37": "Onboarding complete!",

"labvisitstep_overline": "Onboarding step 4",
"labvisitstep_headline": "Go to the lab",
"labvisitstep_subtitle": "The lab reception will already have all your information when you visit. You have received your purchase confirmations and lab visit information by email.",
"labvisitstep_confirm_prompt": "Please confirm your lab visit:",
"labvisitstep_change_prompt": "Want to change your lab appointment?",
"labvisitstep_change_dialog_title": "Change lab appointment",
"labvisitstep_change_info": "We recommend checking the lab's website directly for any changes to its opening hours.",
"labvisitstep_change_contact": "If you have any questions, feel free to send an email to office@speak23.com at any time.",
"labvisitstep_change_success": "Lab appointment updated successfully.",

"labvisitstep_prep_title": "What to know about your lab visit",
"labvisitstep_prep_important_label": "Important:",
"labvisitstep_prep_important_text": "In most labs, you can simply walk in at any time during opening hours. Please check the lab's website to see whether it requires a fixed appointment.",
"labvisitstep_prep_health_label": "Health notice:",
"labvisitstep_prep_health_text": "Please do not visit the lab or have blood drawn if you are currently sick or experiencing symptoms. An illness can affect your blood values and lead to inaccurate results.",
"labvisitstep_prep_whathappens_heading": "What happens at the lab",
"labvisitstep_prep_whathappens_item1": "Trained staff will draw your blood.",
"labvisitstep_prep_whathappens_item2": "If you chose the iFOB colorectal cancer screening, the lab will give you a stool test kit to use at home, which you then return.",
"labvisitstep_prep_prep_heading": "Good preparation matters",
"labvisitstep_prep_prep_lead": "For accurate results, please follow these guidelines:",
"labvisitstep_prep_guideline_fasting_label": "Fasting",
"labvisitstep_prep_guideline_fasting_text": "Don't eat anything for 8–12 hours before your appointment",
"labvisitstep_prep_guideline_hydration_label": "Hydration",
"labvisitstep_prep_guideline_hydration_text": "Drinking water is allowed and recommended",
"labvisitstep_prep_guideline_alcohol_label": "Alcohol",
"labvisitstep_prep_guideline_alcohol_text": "Avoid alcohol the day before",
"labvisitstep_prep_guideline_morning_label": "On the morning of your appointment",
"labvisitstep_prep_guideline_morning_text": "Avoid coffee, tea, or juice",
"labvisitstep_prep_guideline_medication_label": "Medication",
"labvisitstep_prep_guideline_medication_text": "Take your regular medication as usual",
"labvisitstep_prep_guideline_supplements_label": "Supplements",
"labvisitstep_prep_guideline_supplements_text": "If possible, pause your supplements 48 hours before the blood draw to avoid possible interference (biotin-containing products ideally 4–6 days before)",
"labvisitstep_prep_guideline_after_label": "After the blood draw",
"labvisitstep_prep_guideline_after_text": "Drink enough water and eat a light meal to help prevent dizziness",

"labvisitstep_tests_prompt": "Which tests did I order?",
"labvisitstep_tests_dialog_title": "What will be tested?",
"labvisitstep_tests_core_label": "Core Health Blood Test (70+ biomarkers)",
"labvisitstep_tests_table_name": "Test",
"labvisitstep_tests_table_price": "Price",
"labvisitstep_complete_button": "Lab visit completed",
"labvisitstep_complete_instructions": "Please let us know once you've completed your lab visit. You can do this by clicking the \"LAB VISIT COMPLETED\" button. You'll then be taken to a page that keeps you informed about the progress of your test evaluation and its completion. Once all test results are available, logging in will automatically take you to the speak23 platform. You'll then have access to, among other things, your health cockpit and the personalized recommendations we've prepared for you.",
"labvisitstep_logout_text": "You can log out now. Good luck with your lab visit. When you log back in here, you'll return to this page.",
"onb_results_wait_eyebrow": "RESULTS IN PROGRESS",
"onb_results_wait_day_prefix": "Day",
"onb_results_wait_day_of": "of 14",
"onb_results_wait_running_long": "Your results are taking a little longer than usual — we'll email you as soon as they're ready. With your login you will be directed the speak23 application.",
"onb_results_wait_lab_header": "Lab automation is at work ...",
"onb_results_wait_lab_text": "After your blood draw, the lab starts a wide variety of processing steps — some automated — depending on which tests you ordered. These include sample preparation, hematology processing, clinical chemistry analysis, coagulation diagnostics, immunology and microbiology processing, and much more. For certain specialty markers, the lab may send samples to specialized companies to achieve high-quality results. This is one of the reasons it takes time for all results to be complete.",
"onb_results_wait_overline": "Onboarding complete",
"onb_results_wait_title": "Waiting for your test results",
"onb_results_wait_actions_header": "Once available, logging in will take you straight to your test results. In the meantime, please complete the questions below.",
"onb_wait_cycle_row": "Cycle questions",
"onb_wait_med_row": "Medication questions",

"onb_tracker_header": "Cycle Tracker",
"onb_tracker1": "One more important step ...",
"onb_tracker2": "Some blood markers are influenced by your current cycle phase.",
"onb_tracker3": "Please set up the cycle tracker to ensure your results are personalized and accurate.",
"onb_tracker4": "If you have a natural cycle and know your recent period dates, enter the start date and duration of your last three menstrual periods.",
"onb_tracker5": "Close this dialog once the cycle tracker is set up.",
"onb_tracker6": "Enter recent cycle data",
"onb_tracker7": "Enter medication data",
"onb_tracker_select": "Please make your selection:",
"onb_tracker_opt1": "I have a natural menstrual cycle",
"onb_tracker_opt2": "I am postmenopausal",
"onb_tracker_opt3": "I use hormonal birth control",
"onb_tracker_opt4": "I am in perimenopause",
"onb_tracker_confirm": "Confirm selection",
"onb_tracker_done": "Thank you — your profile has been saved.",

"cyc_setup_title": "Set up your cycle tracker",
"cyc_setup_desc": "Mark your last 3 period (menstruation start and end date). We calculate your average cycle phases based on it.",
"cyc_period": "Period",
"cyc_of": "of",
"cyc_start": "Start:",
"cyc_end": "End:",
"cyc_btn_next": "Next Period →",
"cyc_btn_save": "Save",
"cyc_warn_select_all": "Please select start and end for all 3 periods.",
"cyc_err_save": "Error saving past periods.",
"cyc_tab_tracker": "Cycle Tracker",
"cyc_tab_symptoms": "Symptoms",
"cyc_tab_history": "History",
"cyc_male_title": "This feature is for female users",
"cyc_male_desc": "The cycle tracker is designed for people with menstrual cycles. It tracks period phases, ovulation, and symptoms — things that don't apply to your profile.",
"cyc_male_note": "If you believe this is shown in error, you can update your gender in your account settings.",

"cyc_admin_title": "Cycle Administration",
"cyc_admin_subtitle": "View and correct menstrual phases",
"cyc_admin_empty": "No cycles recorded yet. Log your first period to start tracking.",
"cyc_admin_phase_details": "Phase Details",
"cyc_admin_edit_period_tooltip": "Edit period",
"cyc_admin_edit_period_title": "Edit Period",
"cyc_admin_start_date": "Start date",
"cyc_admin_end_date": "End date",
"cyc_admin_duration": "Duration",
"cyc_admin_save_period": "Save Period",
"cyc_admin_new_period_btn": "New Period Started",
"cyc_admin_new_period_desc": "All phases will be calculated from this date using your average cycle data.",
"cyc_admin_period_start_date": "Period start date",
"cyc_admin_delete_tooltip": "Delete cycle",
"cyc_admin_delete_confirm": "Delete this cycle and all its data?",
"cyc_status_active": "Active",
"cyc_status_completed": "Completed",
"cyc_status_predicted": "Predicted",
"cyc_dow_su": "Su", "cyc_dow_mo": "Mo", "cyc_dow_tu": "Tu", "cyc_dow_we": "We",
"cyc_dow_th": "Th", "cyc_dow_fr": "Fr", "cyc_dow_sa": "Sa",
"cyc_phase_menstrual": "Menstrual",
"cyc_phase_follicular": "Follicular",
"cyc_phase_ovulation": "Ovulation",
"cyc_phase_luteal": "Luteal",
"cyc_summary_day_label": "Cycle Day",
"cyc_summary_next_period_label": "Next period in",
"cyc_days_singular": "day",
"cyc_days_plural": "days",
"cyc_today": "today",
"cyc_saving": "Saving…",
"cyc_cancel": "Cancel",
"cyc_confirm": "Confirm",
"cyc_delete": "Delete",
"cyc_notify_period_updated": "Period updated",
"cyc_notify_period_update_fail": "Failed to update period",
"cyc_notify_period_started": "New period started",
"cyc_notify_period_start_fail": "Failed to start new period",
"cyc_notify_cycle_deleted": "Cycle deleted",
"cyc_notify_cycle_delete_fail": "Failed to delete cycle",

"cyc_gate_perimenopause_title": "Cycle Tracker not available",
"cyc_gate_perimenopause_desc": "Your hormonal profile is set to Perimenopause. The cycle tracker is designed for users with an active menstrual cycle.",
"cyc_gate_postmenopause_title": "Cycle Tracker not available",
"cyc_gate_postmenopause_desc": "Your hormonal profile is set to Postmenopause. The cycle tracker is designed for users with an active menstrual cycle.",
"cyc_gate_birthcontrol_title": "Cycle Tracker not available",
"cyc_gate_birthcontrol_desc": "Your hormonal profile is set to Hormonal Birth Control. The cycle tracker is designed for users with a natural menstrual cycle.",
"cyc_gate_noprofile_title": "Hormonal profile not set up",
"cyc_gate_noprofile_desc": "Your female hormonal profile has not been configured yet. Please complete your profile to access the cycle tracker.",
"cyc_gate_change_hint": "You can change your hormonal profile status in your account settings at any time.",
"cyc_gate_profile_btn": "Go to Profile Settings",

"cyc_symptom_log_tooltip": "Symptom log",
"cyc_symptom_log_empty": "No symptoms logged for this phase yet.",
"cyc_symptom_add_btn": "Add Symptom",
"cyc_symptom_date": "Date",
"cyc_symptom_category": "Category",
"cyc_symptom_name": "Symptom",
"cyc_symptom_severity": "Severity",
"cyc_symptom_notes": "Notes (optional)",
"cyc_symptom_save": "Save",
"cyc_symptom_sev_mild": "Mild",
"cyc_symptom_sev_moderate": "Moderate",
"cyc_symptom_sev_severe": "Severe",
"cyc_symptom_cat_physical": "Physical",
"cyc_symptom_cat_emotional": "Emotional",
"cyc_symptom_cat_energy": "Energy",
"cyc_symptom_cat_sleep": "Sleep",
"cyc_symptom_cat_appetite": "Appetite",
"cyc_symptom_cat_skin": "Skin",
"cyc_symptom_cat_other": "Other",
"cyc_symptom_notify_added": "Symptom logged",
"cyc_symptom_notify_add_fail": "Failed to log symptom",
"cyc_symptom_notify_deleted": "Symptom deleted",
"cyc_symptom_notify_delete_fail": "Failed to delete symptom",

"cyc_report_tooltip": "Symptom Report",
"cyc_report_title": "Symptom Report",
"cyc_report_empty": "No symptoms have been logged yet.",
"cyc_report_total": "Total logged",
"cyc_report_by_severity": "By severity",
"cyc_report_by_category": "By category",
"cyc_report_cycle_from": "Cycle from",
"cyc_report_phase_label": "Phase",
"cyc_report_date_label": "Date",
"cyc_report_close": "Close",

"onb_med_header": "Medication List",
"onb_med1": "Do you take any medication on a regular basis?",
"onb_med2": "If yes, please add your medication to your medication list.",
"onb_med3": "This helps us personalize your analysis and account for any interactions.",
"onb_med4": "Open the dialog and enter your medication details.",
"onb_med5": "Close this dialog when your list is complete.",
"onb_med_intro": "Do you take medication regularly? You can complete your medication list here. This allows your analyses to be better personalized and helps account for possible interactions.",
"onb_med_done": "Medication list saved successfully.",
"onb_med_none": "I do not take any medication.",
"onb_web_header": "Health News & Education",
"onb_web_text": "We regularly publish new articles, research insights, and educational content about preventive health. Check back at speak23.com while you wait — there's always something new to discover.",
"onb_web_cta": "Visit speak23.com",
"onb_email_header": "Questions or Feedback?",
"onb_email_text": "Have a question, ran into an issue during your lab visit, or just want to share feedback? You can reach us anytime at office@speak23.com — we're happy to help.",

"med_headline": "YOUR CURRENT MEDICATION",
"med_add_btn": "Add Medication",
"med_fetch_error": "Failed to fetch medication list.",
"med_fetch_loading": "Fetching data...",
"med_updated": "Record updated successfully!",
"med_update_error": "Error updating record",
"med_deleted": "Record deleted successfully!",
"med_delete_error": "Error deleting record",
"med_added": "Record added successfully!",
"med_uploading": "Uploading data...",
"med_col_start": "Start Date",
"med_col_end": "End Date",
"med_col_drug": "Drugname",
"med_col_indication": "Indication",
"med_col_dose": "Dose",
"med_col_route": "Route",
"med_col_frequency": "Frequency",
"med_col_status": "Status",
"med_col_notes": "Notes",
"med_col_update": "Update",
"med_col_delete": "Delete",
"med_dia_update": "Update Medication Record",
"med_dia_delete": "Delete Medication Record",
"med_dia_add": "Add Medication Record",
"med_form_drug": "Drug Name",
"med_form_indication": "Indication",
"med_form_dosing": "Dosing",
"med_form_route": "Route",
"med_form_frequency": "Frequency",
"med_form_start": "Start Date",
"med_form_end": "End Date",
"med_form_status": "Status",
"med_status_active": "Active",
"med_status_inactive": "Inactive",
"med_status_discontinued": "Discontinued",
"med_form_notes": "Notes",
"med_form_submit": "Submit",
"med_confirm_delete": "Are you sure you want to delete this record?",
"med_btn_confirm_delete": "Confirm Delete",
"med_no_items": "No medication entries yet.",
"med_intro": "Please keep your medication list up-to-date. Speak23 needs your personal drug consumption for more precise recommendations together with your lab results. Add new medication by pressing the ADD button, or edit existing entries with the editing buttons provided.",

"onb_lab00": "Schedule Your Lab Visit",
"onb_lab01": "Please continue with Step 3:",
"onb_lab0a": "A summary of your order and full preparation instructions have been sent to you by email.",
"onb_lab0b": "Important: come fasting — no food, water only. Avoid supplements for a few days before. Lab visits are best done in the morning.",
"onb_lab_hours_note": "Please make sure your appointment falls within the lab's opening hours.",
"onb_lab_datetime_change_note": "You can still change this appointment later.",
"onb_lab1": "Select a certified lab and choose your preferred date and time (make sure your appointment falls within the lab's opening hours):",
"onb_lab2": "Your order summary and payment confirmation:",
"onb_lab3": "Pick a date",
"onb_lab4": "Pick a time",
"onb_lab5": "Select a lab",
"onb_lab_select_heading": "Choose a lab",
"onb_lab_personal_heading": "Complete your personal details",
"onb_lab_personal_note1": "The lab reception will already have this information for your visit.",
"onb_lab_personal_note2": "This makes your lab check-in fast and efficient.",
"onb_lab_accordion_hint": "You can open and close each item by clicking on it for a better overview.",
"onb_lab6": "Your Membership",
"onb_lab7": "Add-On packages for your next lab visit",
"onb_labb": "Confirm",
"onb_lab_datetime_heading": "Pick your date & time",
"onb_lab_review_heading": "Review your selection and confirm",
"onb_lab_loading": "Loading labs...",
"onb_lab_err_load": "Unable to load labs. Please try again later.",
"onb_lab_err_load_option": "Error loading labs",
"onb_lab_success": "Lab visit scheduled successfully!",
"onb_lab_info_address": "Address",
"onb_lab_info_phone": "Phone",
"onb_lab_info_email": "Email",
"onb_lab_info_notes": "Notes",
"onb_lab_blood_draw_until": "Blood draw until:",
"onb_lab_contact_tooltip": "Contact Details Laboratory",
"onb_lab_contact_title": "Lab – Contact Details",

"onb_quest1": "Last step: please complete a short questionnaire about your health.",
"onb_quest2": "It takes about 5 minutes and helps us analyze your results in a personalized way.",
"onb_quest3": "You ordered a gut microbiome analysis.",
"onb_quest35": "Your stool collection kit will arrive within 2–3 days.",
"onb_quest36": "Your Stool Sample Kit",
"onb_quest37": "Please wait for your kit to arrive by post.",
"onb_quest38": "You can log out for now.",
"onb_quest39": "Sign back in once your kit has arrived.",
"onb_quest41": "Welcome back.",
"onb_quest42": "Please follow the instructions in your stool sample kit.",
"onb_quest4": "After collecting your stool sample,",
"onb_quest5": "please complete and submit the short questionnaire.",
"onb_quest_submit": "Submit Questionnaire",
"onb_quest_submitting": "Submitting…",
"onb_quest_form_title": "Questionnaire & personal context",
"onb_quest_open": "Open questionnaire",
"onb_quest_next": "Next question",
"onb_quest_prev": "Back",
"onb_quest_form_subtitle": "A carefully completed questionnaire improves the analysis and, above all, the personalized recommendations. Your answers carry significant weight in the overall assessment — for more healthy years. Thank you for your patience and care.",
"onb_quest_answered_label": "answered",
"onb_quest_no_answer": "No answer",
"onb_quest_submit_success": "Questionnaire submitted successfully!",
"onb_quest6": "Step-by-step instructions are included in the kit.",
"onb_quest_err_no_visit": "No lab visit found.",
"onb_quest_err_finalize": "Failed to finalize lab visit.",
"onb_quest_err_no_kit": "No stool sample kit has been assigned yet.",
"onb_quest_err_kit_load": "Failed to load stool sample kit.",
"onb_quest_kit_id": "Kit ID",
"onb_quest_act_key": "Activation Key",
"microstep_overline": "Onboarding step 3 - with gut microbiome add-on",
"microstep_title": "Microbiome sample and questionnaire",
"microstep_labvisit_title": "Your lab visit",
"microstep_kit_none": "No sample kit has been assigned yet.",
"microstep_card_step1": "Once you have received the test kit, follow the steps in the enclosed instructions and send the stool sample in the prepaid envelope.",
"microstep_card_step2": "Then please fill out the questionnaire:",
"microstep_step1": "Please wait until your sample kit for the gut microbiome test arrives by post.",
"microstep_step2": "Once you've received the sample kit, follow the steps in the enclosed instructions and send the sample back in the prepaid envelope. Then fill out the questionnaire and submit it using the SUBMIT QUESTIONNAIRE button.",
"microstep_step3": "If you complete your lab appointment before finishing the questionnaire, you'll find all the relevant information about it below the questionnaire. You can also change your lab appointment there if needed. Please briefly confirm your lab visit using the LAB VISIT COMPLETED button.",
"microstep_step4": "Send the sample tube back by post in the enclosed envelope to the prepaid address.",
"microstep_closing_text": "You can log out now. Once you've successfully collected your stool sample for the microbiome test, please log back in. You'll return to this page to fill out the questionnaire. Good luck!",
"microstep_kit_header": "Your Stool Sample Kit for the Gut Microbiome Analysis",
"microstep_kit_note": "You don't need to remember the numbers in the box. They help with lab logistics.",
"microstep_kit_activated": "Your stool sample kit is already activated for you.",
"microstep_address_label": "Shipping address for your sample kit",
"microstep_address_change_prompt": "Would you like to change the address the sample kit is sent to?",
"microstep_address_empty": "Please provide the shipping address for your sample kit.",
"address_edit": "Change address",
"address_name": "Name",
"address_line1": "Street and house number",
"address_line2": "Address line 2 (optional)",
"address_postal_code": "Postal code",
"address_city": "City",
"address_country": "Country (e.g. DE)",
"address_save": "Save",
"address_save_error": "Failed to save the address.",
"ua_address_card_headline": "Shipping Address",
"ua_address_card_subtext": "Manage the address used for shipping your test kits.",
"ua_address_label": "Shipping address",
"ua_address_change_prompt": "Would you like to change your shipping address?",
"ua_address_empty": "Please provide your shipping address.",

"onb_psa_verifying": "Verifying your payment...",
"onb_psa_error_title": "Something went wrong verifying your payment.",
"onb_psa_try_again": "Try Again",
"onb_psa_success_title": "Payment Successful!",
"onb_psa_redirecting": "Redirecting you to the next step of onboarding...",

"onb_pe_title": "Subscription Payment Failed",
"onb_pe_body": "Your subscription payment could not be completed. No charges were made. Please try again or reach out to support if you need assistance completing your subscription.",
"onb_pe_try_again": "Try Again",
"onb_pe_contact_support": "Contact Support",

"onb_ps_verifying": "Verifying your payment...",
"onb_ps_auth_required": "Authentication required. Please log in.",
"onb_ps_confirmed": "Payment confirmed successfully!",
"onb_ps_received": "Payment received.",
"onb_ps_verify_failed": "Payment verification failed. Please contact support.",
"onb_ps_missing_session": "Missing session ID.",
"onb_ps_error_title": "Payment Error",
"onb_ps_try_again": "Try Again",
"onb_ps_success_title": "Payment Successful! Wait to be redirected ...",
"onb_ps_processing": "Processing Payment...",
"onb_ps_continue": "CONTINUE",

"onb_wait_no_data": "No data found.",
"onb_wait_per_year": "per year",
"onb_wait_payment_ok": "✅ Payment confirmed",
"onb_wait_payment_fail": "❌ Not paid",
"onb_wait_no_membership": "No membership info found.",
"onb_wait_unnamed_test": "Unnamed Test",
"onb_wait_addon_total": "Addon Packages — Total:",
"onb_wait_no_addons": "No Add-On Tests selected for next lab visit.",

"onb_srb_verifying": "Verifying your payment...",
"onb_srb_confirming": "Confirming with Stripe...",
"onb_srb_almost": "Almost there...",
"onb_srb_long_wait": "This is taking longer than usual, please wait...",
"onb_srb_finalizing": "Finalizing Your Checkout...",
"onb_srb_redirect_failed": "Redirect Failed",
"onb_srb_invalid_session": "Invalid or missing Stripe session ID.",
"onb_srb_generic_error": "Something went wrong while finalizing your payment.",
"onb_srb_timeout": "Request timed out. Please try again.",
"onb_srb_session_not_found": "Session not found. Your payment may have already been processed.",
"onb_srb_invalid_session_short": "Invalid session.",
"onb_srb_return": "RETURN",

"onb_ss_title": "Finalizing your subscription...",
"onb_ss_confirming": "Confirming your payment...",
"onb_ss_setting_up": "Setting up your first lab visit...",
"onb_ss_long_wait": "This is taking a little longer than usual, please hold on...",
"onb_ss_success_title": "Payment confirmed!",
"onb_ss_success_body": "Taking you to your lab visit...",
"onb_ss_missing_session_title": "Something's not right",
"onb_ss_missing_session_body": "We couldn't find your checkout session. Please return to the subscription page and try again.",
"onb_ss_timeout_title": "Still working on it",
"onb_ss_timeout_body": "Your payment is taking a little longer to confirm than usual. You can try again, or reach out if this continues.",
"onb_ss_retry": "Retry",
"onb_ss_contact_support": "Contact Support",

"onb_oas_title": "Finalizing your add-on purchase...",
"onb_oas_setting_up": "Linking your add-on order to your lab visit...",
"onb_oas_success_body": "Taking you to the next step...",
"onb_oas_missing_session_body": "We couldn't find your checkout session. Please return to the add-on selection page and try again.",

"onb_snc_title": "Payment Canceled",
"onb_snc_body": "Your checkout was canceled. You can return to the checkout page to try again.",
"onb_snc_retry": "Retry Checkout",
"onb_snc_logout": "Logout",


//  ########## Card Core Health Test Info (Configurator) ###############

    "track_start_with": "Start with the",
    "track_slide_1_title": "Core Health Blood Test",
    
    "track_blood_explanation": "Test 2x or 4x a year in a certified lab for",
    "track_blood_explanation2": " 75+ biomarkers ",
    "track_blood_explanation3": "across these 4 essential health areas.",
    "track_core_1": "Metabolic Health",
    "track_core_2": "Immune Health",
    "track_core_3": "Organ Function",
    "track_core_4": "Micronutrients",

    "track_markers_label": "markers",
    "track_overview_label": "Overview",

    "metabolism_detail_desc": "How your body converts food into energy, manages blood sugar, and processes fats.",
    "marker_metabolism_1": "Fasting Glucose",
    "marker_metabolism_2": "HbA1c",
    "marker_metabolism_3": "eAG",
    "marker_metabolism_4": "Total Cholesterol (TC)",
    "marker_metabolism_5": "LDL Cholesterol",
    "marker_metabolism_6": "HDL Cholesterol",
    "marker_metabolism_7": "Non-HDL Cholesterol",
    "marker_metabolism_8": "Triglycerides",
    "marker_metabolism_9": "Apolipoprotein B",
    "marker_metabolism_10": "LDL/HDL ratio",
    "marker_metabolism_11": "TC/HDL ratio",
    "marker_metabolism_12": "GGT/HDL ratio",
    "marker_metabolism_13": "Triglycerides/HDL ratio",
    
    "immune_detail_desc": "Your body's defense system, indicating how well it handles inflammation, fights infections, and recovers from stress.",
    "marker_immune_1": "WBC",
    "marker_immune_2": "RBC",
    "marker_immune_3": "RDW",
    "marker_immune_4": "Hemoglobin",
    "marker_immune_5": "Hematocrit",
    "marker_immune_6": "Platelets (PLT)",
    "marker_immune_7": "Mean Platelet Volume (MPV)",
    "marker_immune_8": "MCV",
    "marker_immune_9": "MCH",
    "marker_immune_10": "MCHC",
    "marker_immune_11": "CRP",
    "marker_immune_12": "ESR",
    "marker_immune_13": "Systemic Immune Inflammation Index",
    "marker_immune_14": "Morphology",
    "marker_immune_15": "Segmented Neutrophils (abs.)",
    "marker_immune_16": "Segmented Neutrophils (%)",
    "marker_immune_17": "Basophils (abs.)",
    "marker_immune_18": "Basophils (%)",
    "marker_immune_19": "Eosinophils (abs.)",
    "marker_immune_20": "Eosinophils (%)",
    "marker_immune_21": "Lymphocytes (abs.)",
    "marker_immune_22": "Lymphocytes (%)",
    "marker_immune_23": "Monocytes (abs.)",
    "marker_immune_24": "Monocytes (%)",
    "marker_immune_25": "Neutrophil/Lymphocyte ratio",
    "marker_immune_26": "Platelet/Lymphocyte ratio",
    "marker_immune_27": "MPV/PLT ratio",
    
    "organ_detail_desc": "The operational health of your vital systems - like the liver and kidneys- that filter toxins, process proteins, and keep your body running smoothly.",
    "marker_organ_1": "AST (GOT)",
    "marker_organ_2": "ALT (GPT)",
    "marker_organ_3": "AST/ALT ratio",
    "marker_organ_4": "GGT",
    "marker_organ_5": "Alkaline Phosphatase (ALP)",
    "marker_organ_6": "Bilirubin",
    "marker_organ_7": "Albumin",
    "marker_organ_8": "Index FIB-4",
    "marker_organ_9": "Creatinine",
    "marker_organ_10": "BUN",
    "marker_organ_11": "BUN/Creatinine ratio",
    "marker_organ_12": "eGFR",
    "marker_organ_13": "Urea",
    "marker_organ_14": "Uric Acid",
    "marker_organ_15": "Lipase",
    "marker_organ_16": "Pancreatic Amylase",
    "marker_organ_17": "CK",
    "marker_organ_18": "LDH",
    "marker_organ_19": "Calcium-Phosphate product",

    "vitamins_detail_desc": "The essential building blocks, minerals, and compounds your body needs to maintain bone density, energy levels, and cellular health.",
    "marker_vitamins_1": "Vitamin D (25-OH-D3)",
    "marker_vitamins_2": "Vitamin B12",
    "marker_vitamins_3": "Magnesium",
    "marker_vitamins_4": "Iron",
    "marker_vitamins_5": "Ferritin",
    "marker_vitamins_6": "Transferrin",
    "marker_vitamins_7": "Transferrin Saturation",
    "marker_vitamins_8": "TIBC (Total Iron-Binding Capacity)",
    "marker_vitamins_9": "UIBC (Unsaturated Iron Binding Capacity)",
    "marker_vitamins_10": "Serum Osmolarity",
    "marker_vitamins_11": "Sodium (Na+)",
    "marker_vitamins_12": "Potassium (K+)",
    "marker_vitamins_13": "Na+/K+ ratio",
    "marker_vitamins_14": "Chloride (Cl-)",
    "marker_vitamins_15": "Calcium (Ca2+)",
    "marker_vitamins_16": "Phosphate",
    "marker_vitamins_17": "Cl-/Na+ ratio",

    "track_see_all_markers": "see all {count} markers",

    "track_extend_with": "Personalize your lab visit with Add-Ons.",

    "card_toggle_label": "What do we test for?",
    


    //#### Add-On Packages Section #####
    "addon_heading": "Add-On Packages",
    "addon_desc": "Tailor any lab visit to your specific goals. You can add specialized diagnostic packages for an additional fee to gain deeper insights exactly where you need them most.",

        // Card: Biological Age
        "card_bio_age_title": "BIOLOGICAL AGE",
        "card_bio_age_desc": "Chronological age tells you how long you've lived. Biological age tells you how well. Your daily habits, stress levels, and nutrition are either accelerating or slowing your aging — this test shows you exactly where you stand.",
        "card_bio_age_markers": "Biological age reflects how your body is aging at a physiological level. Using the PhenoAge clock, we assess biological age, pace of aging, and key aging drivers to help you understand long-term health beyond chronological age.",

        // Card: Gut Microbiome Analysis
        "card_gut_title": "GUT MICROBIOME ANALYSIS",
        "card_gut_desc": "The gut is often called the second brain — and for good reason. It shapes your immunity, drives your mood, and sits at the center of your metabolic health. Most people never look at it. This test does.",
        "card_gut_markers": "Understand your microbial balance and diversity. Discover how your gut bacteria interact with your immunity, mood, and inflammation. Explore patterns linked to leaky gut, SIBO, and food sensitivities, while uncovering connections to mental clarity, stress response, and metabolic health (including blood sugar, liver, and thyroid pathways).",

        // Card: Vitamins & Micronutrients
        "card_vitamins_title": "ADVANCED VITAMINS & TRACE ELEMENTS",
        "card_vitamins_desc": "Advanced nutrient profiling: covering key vitamins and trace minerals beyond the essentials for deeper insights into your nutritional health.",
        "card_vitamins_markers": "Vitamin B1, Vitamin B2, Vitamin B6, Biotin (Vitamin B7), Folate (Vitamin B9), Vitamin K1, Vitamin K2, Beta-Carotene (Provitamin A), Retinol (Vitamin A), Vitamin E, Ascorbic Acid (Vitamin C), Zinc, Copper, Selenium",

        // Card: Hormone & Fertility Female
        "card_female_hormone_title": "HORMONE & FERTILITY (FEMALE)",
        "card_female_hormone_desc": "Designed to uncover hormonal imbalances linked to cycle irregularities, fertility concerns, and menopausal symptoms.",
        "card_female_hormone_markers": "Estradiol (E2), Testosterone, Luteinizing Hormone (LH), Follicle-Stimulating Hormone (FSH), Progesterone (P4), Prolactin, Bioavailable Testosterone, SHBG, DHEA-S, Testosterone/E2 ratio",

        // Card: Ovarian Reserve
        "card_ovarian_title": "OVARIAN RESERVE (FEMALE)",
        "card_ovarian_desc": "Provides a detailed assessment of ovarian egg reserve, reflecting the remaining number of eggs and offering insight into ovarian function and reproductive potential over time.",
        "card_ovarian_markers": "Anti-Müllerian Hormone (AMH)",

        // Card: Energy & Hormonal Balance For Men
        "card_male_hormone_title": "ENERGY, LIBIDO & HORMONAL BALANCE (MEN)",
        "card_male_hormone_desc": "A comprehensive view of the hormonal drivers of male sexual health, energy, and long-term vitality.",
        "card_male_hormone_markers": "Estradiol (E2), Testosterone, Luteinizing Hormone (LH), Follicle-Stimulating Hormone (FSH), Progesterone (P4), Prolactin, Bioavailable Testosterone, SHBG, DHEA-S, Testosterone/E2 ratio",

        // Card: Prostate Cancer
        "card_prostate_title": "PROSTATE CANCER PREVENTION & EARLY AWARENESS (MEN)",
        "card_prostate_desc": "A simple blood-based insight into prostate health, supporting early awareness of prostate cancer risk as part of preventive care.",
        "card_prostate_markers": "Prostate-specific antigen (PSA)",

        // Card: Thyroid Health
        "card_thyroid_title": "THYROID HEALTH",
        "card_thyroid_desc": "Complete thyroid assessment for metabolism, energy, and reproductive health in both women and men.",
        "card_thyroid_markers": "Thyroid-Stimulating Hormone (TSH), free T3, free T4, Calcitonin",

        // Card: Omega Health Lifestyle
        "card_omega_title": "OMEGA-3 INDEX & FATTY ACID PROFILE",
        "card_omega_desc": "Optimizing essential fatty acids to support brain function, cognitive performance, and healthy inflammatory balance for long-term wellness.",
        "card_omega_markers": "Omega-3 Fatty Acids, Omega-6 Fatty Acids, Monounsaturated Fatty Acids (MUFA), Saturated Fatty Acids (SFA), Polyunsaturated Fatty Acids (PUFA), Fatty Acid Ratios & Indices (Omega-6/Omega-3 ratio, SFA/MUFA ratio, AA/DGLA, AA/EPA)",

        // Card: Atherosclerosis Prevention
        "card_athero_title": "ATHEROSCLEROSIS PREVENTION",
        "card_athero_desc": "Integrated assessment of lipid, glucose, and metabolic health, with markers for insulin sensitivity, oxidative stress, and cardiovascular risk.",
        "card_athero_markers": "Lp(a), Insulin, QUICKI, HOMA-IR, Homocysteine, Coenzyme Q10, Atherogenic Index of Plasma (AIP), Triglycerides-Glucose Index",

        // Card: Bones Health
        "card_bones_title": "BONES HEALTH",
        "card_bones_desc": "Evaluation of bone metabolism through markers of bone formation and resorption, providing insights into bone strength, turnover, and overall skeletal health.",
        "card_bones_markers": "Beta-Crosslaps (CTX), Parathyroid Hormone (PTH), Osteocalcin",

        // Card: Colon Cancer Prevention
        "card_colon_title": "COLON CANCER PREVENTION & EARLY AWARENESS",
        "card_colon_desc": "Non-invasive stool test for early detection of blood in the stool to support colon cancer prevention.",
        "card_colon_markers": "Immunochemical Fecal Occult Blood (iFOB) Test",









//############### LAB VISIT SCHEDULING WORKFLOW ######################
"acc_header": "ACCOUNT — ",
"acc_header1": "YOUR LAB VISITS",

"acc_visit1": "Preventive health is built on regular testing and timely action.",
"acc_visit2": "Please plan and book your next lab visit based on your subscription.",
"acc_visit3": "Here's an overview of the workflow for your next lab visit.",

"acc_visit4": "Date:",
"acc_visit5": "Time:",
"acc_visit6": "Laboratory:",
"acc_visit7": "Add-On Test Packages:",
"acc_visit8": "Status:",

"acc_visit00": "No lab visits scheduled yet",

"acc_header2": "PLAN YOUR UPCOMING LAB VISITS",
"acc_header3": "",

"visitplan_1": "Based on your subscription, here are suggested dates for your upcoming lab visits.",
"acc_visit9": "The suggested date is a proposal — adjust it to suit you.",

"acc_visit10": "Start the workflow for your next lab visit",
"acc_visit11": "Below you'll find a proposed schedule for your upcoming lab visits. Select a date and click 'CONFIRM DATE' to start the booking workflow.",

"acc_visit12": "Lab visits in current subscription period:",
"acc_visit13": "Book your next lab visit",
"acc_visit14": "Your current subscription plan and the number of included Core Health Blood Tests are shown below, along with a history of all past lab visits and any add-on packages ordered.",

"acc_visit15": "Date",
"acc_visit16": "Laboratory",
"acc_visit17": "Add-On Packages Ordered",
"acc_visit18": "Booking your next lab visit takes just a few minutes and involves 3 simple steps:",
"acc_visit19": "1. Choose your preferred lab, date and time",
"acc_visit20": "2. Optional: select & pay for Add-On packages for this lab visit",
"acc_visit21": "3. Complete a short questionnaire",

"acc_visit28": "Visit No.",
"acc_visit22": "Status",
"acc_visit23": "Continue",
"acc_visit24": "Continue",
"acc_visit25": "Continue",
"acc_visit26": "Waiting for lab report",
"acc_visit27": "Lab report available",

"acc_visit30": "BACK TO LAB VISITS OVERVIEW",
"acc_visit_info_title": "Information",
"acc_visit_steps_title": "Steps overview",
"acc_visit_steps_text": "Booking your next lab visit takes a few simple steps that don't take much time:",
"acc_visit_sub_title": "Your membership",
"acc_visit_completed": "COMPLETED",
"acc_visit_pending": "Pending",
"acc_visit_err_sub": "Failed to fetch subscription status.",
"acc_visit_err_visits": "Failed to load lab visits.",
"acc_visit_no_sub": "No subscription status found.",

"account_quest1": "Select a date to view your questionnaire responses:",

"visitplan_2": "Subscription plan:",
"visitplan_3": "Current period start date:",
"visitplan_4": "Auto-renewal:",
"visitplan_5": "Days remaining in current period:",
"visitplan_6": "Total tests included:",
"visitplan_7": "Tests used:",
"visitplan_8": "Tests remaining:",

"visitplan_title": "Here's a suggested schedule for your upcoming lab visits. Select a date and start the booking workflow.",
"visitplan_9": "Last visit:",
"visitplan_10": ". suggested date",
"visitplan_11": "Book next visit",

"visitplan_12": "Booking status:",

"visitplan_13": "Scheduled lab visits:",
"visitplan_14": "Not yet selected",

"lab_header": "LAB VISIT — ",
"lab_header2": "SELECT ADD-ONS (OPTIONAL)",

"select_all": "Select all",
"deselect_all": "Deselect all",
"lab_step1": "Choose lab, date and time",
"lab_step2": "Choose add-on tests (optional)",
"lab_step3": "Short questionnaire (update)",
"lab_step4": "Lab visit",

"lab_quest_review_title1": "What has changed since your",
"lab_quest_review_title2": "last test?",
"lab_quest_review_desc": "So we can put your new results into context, we'd like to briefly know what has changed since your last lab visit.",
"lab_quest_review_info_title": "Your previous answers have been carried over.",
"lab_quest_review_info_desc": "Open the questionnaire and check everything's correct. Submit the questionnaire also if no corrections are made.",
"lab_quest_review_btn": "Open questionnaire",

"lab_micro_quest_desc": "Fill out the short questionnaire for your stool test.",
"lab_micro_quest_done": "Your questionnaire has been submitted.",

"lab_action1_header": "Action 1: Update and submit the questionnaire",
"lab_action2_header": "Action 2: Log your interventions and progress",

"lab_checkin_subtitle": "Tell us what you've actually put into practice.",
"lab_checkin_recommended_on": "Recommended on",
"lab_checkin_question": "Have you implemented this recommendation?",
"lab_checkin_status_yes": "Done",
"lab_checkin_status_partial": "Partially",
"lab_checkin_status_no": "Not done",
"lab_checkin_note_placeholder": "What exactly did you change? For example: adjusted the dosage, took it regularly, or discussed it with a doctor.",
"lab_checkin_other_title": "Have you changed anything else?",
"lab_checkin_other_subtitle": "Add any changes that weren't part of your recommendations — e.g. diet, exercise, sleep, medication, supplements, or your life situation.",
"lab_checkin_other_placeholder": "Briefly describe what has changed since your last test.",
"lab_checkin_submit": "Submit feedback",
"lab_checkin_submitting": "Saving…",
"lab_checkin_submit_success": "Thanks! Your feedback has been saved.",

"lab_start1": "Your current subscription:",
"lab_start11": "Your planned lab visit date:",
"lab_start2": "This workflow helps you organize your next lab visit as part of your membership.",
"lab_start3": "In addition to your Core Health Blood Test, which is included,",
"lab_start4": "you can optionally add extra test packages for this lab visit.",
"lab_start45": "Click 'SECURE PAYMENT' to pay for any selected add-ons.",
"lab_start5": "Don't want any add-ons this time?",
"lab_start6": "Skip ahead to Step 3 to complete the questionnaire.",
"lab_start7": "Go directly to Step 3",

"lab_header3": "LAB VISIT — ",
"lab_header4": "LAB, DATE & TIME",
"lab_start8": "Continue with Step 3:",
"lab_start9": "Choose your preferred lab and book a date and time within its opening hours.",
"lab_start10": "You can still change your preferred lab visit date directly with the selected lab.",

"lab_header5": "LAB VISIT — ",
"lab_header6": "QUESTIONNAIRE",
"lab_header7": "LAB VISIT — ",
"lab_header8": "SUMMARY",
"lab_header9": "LAB VISIT — ",
"lab_header10": "CONFIRM YOUR VISIT",
"labvisitstepadmin_complete_instructions": "Please click the \"LAB VISIT COMPLETED\" button once you have successfully completed your lab visit. You can check on the progress of your results evaluation under ADMINISTRATION / LAB VISITS in the navigation menu. Once all results are available, you'll find them as usual in the speak23 software, including your personalized recommendations.",

"waiting_1": "Order summary and payment confirmation:",
"waiting_2": "A confirmation email with all lab visit details and full preparations has been sent to you.",


};
